import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Search, MapPin, Clock, AlertTriangle, Users, Eye, Shield, ChevronRight, Menu, X } from 'lucide-react';
import axios from 'axios';
// PWA install prompts are available in-app (installable as a home-screen
// app). SiteFooter is rendered globally by App.js (see GlobalFooter). Kept
// out of this file to prevent double-rendering on the landing page.

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";

export default function LandingPage() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_cases: 0, found_cases: 0, active_cases: 0, total_sightings: 0 });
  const [recentCases, setRecentCases] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchRecentCases();
    // Track QR scan (fire-and-forget). Stamps once per browser per day.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('ref') === 'qr') {
        const today = new Date().toISOString().slice(0, 10);
        const key = `misyou.qrLogged.${today}`;
        if (!localStorage.getItem(key)) {
          axios.post(`${API}/qr-scans/log`, {
            referrer: document.referrer || '',
            path: window.location.pathname || '/',
          }).then(() => {
            localStorage.setItem(key, '1');
          }).catch((err) => {
            console.warn('[landing] scan-log beacon failed (non-fatal):', err?.message || err);
          });
        }
      }
    } catch (err) {
      // Whole scan-log block is best-effort — never block the landing page render.
      console.warn('[landing] QR-scan log setup failed (non-fatal):', err?.message || err);
    }
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchRecentCases = async () => {
    try {
      const response = await axios.get(`${API}/cases?limit=6&sort_by=created_at&order=desc`);
      setRecentCases(response.data);
    } catch (error) {
      console.error('Failed to fetch cases:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="MISYOU" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-black text-slate-900">MISYOU</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/search" className="text-slate-600 hover:text-primary font-medium transition-colors">
                Search
              </Link>
              <Link to="/pricing" className="text-slate-600 hover:text-primary font-medium transition-colors" data-testid="nav-pricing-link">
                Pricing
              </Link>
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className="text-slate-600 hover:text-primary font-medium transition-colors">
                    Dashboard
                  </Link>
                  <Link to="/report" data-testid="report-case-nav">
                    <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6 btn-press">
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Report Missing
                    </Button>
                  </Link>
                  <Button variant="ghost" onClick={logout} className="text-slate-600">
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-slate-600">Sign In</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-primary hover:bg-primary/90 rounded-full px-6 btn-press">
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200">
              <nav className="flex flex-col gap-3">
                <Link to="/search" className="px-2 py-2 text-slate-600 hover:text-primary">Search</Link>
                <Link to="/pricing" className="px-2 py-2 text-slate-600 hover:text-primary" data-testid="mobile-nav-pricing">Pricing</Link>
                {isAuthenticated ? (
                  <>
                    <Link to="/dashboard" className="px-2 py-2 text-slate-600 hover:text-primary">Dashboard</Link>
                    <Link to="/report" className="px-2 py-2">
                      <Button className="w-full bg-red-600 hover:bg-red-700 text-white">Report Missing</Button>
                    </Link>
                    <Button variant="ghost" onClick={logout} className="text-left px-2">Logout</Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="px-2 py-2 text-slate-600">Sign In</Link>
                    <Link to="/register" className="px-2">
                      <Button className="w-full bg-primary">Get Started</Button>
                    </Link>
                  </>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 map-pattern opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <Badge className="mb-6 bg-red-100 text-red-700 border-red-200 px-4 py-1.5 text-sm font-semibold">
              <AlertTriangle className="w-4 h-4 mr-2" />
              {stats.active_cases} Active Cases
            </Badge>
            
            <h1 className="text-5xl md:text-7xl font-black text-slate-900 leading-none mb-6">
              Help Find<br />
              <span className="text-primary">Missing Persons</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl">
              Every minute counts. Join our community-powered platform to help reunite families with their loved ones.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex gap-3 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by name, location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 text-lg bg-white border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20"
                  data-testid="hero-search-input"
                />
              </div>
              <Button 
                type="submit" 
                className="h-14 px-8 bg-primary hover:bg-primary/90 rounded-xl text-lg btn-press"
                data-testid="hero-search-btn"
              >
                Search
              </Button>
            </form>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-4">
              <Link to={isAuthenticated ? "/report" : "/register"}>
                <Button 
                  size="lg" 
                  className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8 shadow-lg shadow-red-500/30 btn-press"
                  data-testid="hero-report-btn"
                >
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  Report Missing Person
                </Button>
              </Link>
              <Link to="/search">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-8 border-slate-300 hover:bg-slate-100 btn-press"
                  data-testid="hero-browse-btn"
                >
                  <Eye className="w-5 h-5 mr-2" />
                  Browse All Cases
                </Button>
              </Link>
              <Link to="/pricing">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 border-slate-300 hover:bg-slate-100 btn-press"
                  data-testid="hero-pricing-btn"
                >
                  View Plans &amp; Pricing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How MISYOU works — plain-English description for compliance
          reviewers (PayU KYC, Paytm KYC, RBI Payment Aggregator audits)
          and for first-time visitors who need a clear "what is this site"
          answer within 10 seconds of landing. */}
      <section className="py-14 bg-slate-50 border-y border-slate-200" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
              How MISYOU works
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
              A subscription-based missing-person reporting &amp; search platform
            </h2>
            <p className="text-sm text-slate-600 mt-3 max-w-3xl mx-auto leading-relaxed">
              MISYOU is a 100% digital <strong>Software-as-a-Service (SaaS)</strong> platform for India.
              Families report a missing person, upload the police FIR, and community members receive
              the case in their feed and submit possible sightings back. Cases stay visible while the
              family maintains an active subscription (₹20/day, no auto-renewal).
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Step 1</p>
              <p className="font-semibold text-slate-900 mt-1">Report the case</p>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                Family creates a listing with the missing person&apos;s photo, description, last-seen
                location and a copy of the police FIR (mandatory). First 15 days are free.
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Step 2</p>
              <p className="font-semibold text-slate-900 mt-1">Community reach</p>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                The case is visible to the community. Anyone can submit an anonymous sighting.
                Photos are AI-screened for CSAM &amp; safety before appearing.
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Step 3</p>
              <p className="font-semibold text-slate-900 mt-1">Extend as needed</p>
              <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                After the free trial, the family subscribes at ₹20/day (no auto-renewal) to keep
                the case visible. All payments processed via PCI-DSS-compliant gateways.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-black text-slate-900">{stats.total_cases}</div>
              <div className="text-sm text-muted-foreground uppercase tracking-wider mt-2">Total Cases</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-black text-green-600">{stats.found_cases}</div>
              <div className="text-sm text-muted-foreground uppercase tracking-wider mt-2">Found Safe</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-black text-red-600">{stats.active_cases}</div>
              <div className="text-sm text-muted-foreground uppercase tracking-wider mt-2">Active Cases</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-black text-primary">{stats.total_sightings}</div>
              <div className="text-sm text-muted-foreground uppercase tracking-wider mt-2">Sightings Reported</div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Cases */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Recent Cases</h2>
              <p className="text-muted-foreground mt-2">Help us find these missing individuals</p>
            </div>
            <Link to="/search">
              <Button variant="outline" className="rounded-full hidden md:flex">
                View All Cases
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentCases.map((caseItem, index) => (
              <Link to={`/case/${caseItem.case_id}`} key={caseItem.case_id}>
                <Card 
                  className="overflow-hidden card-hover bg-white border-slate-100 shadow-xl shadow-slate-200/50"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`case-card-${caseItem.case_id}`}
                >
                  <div className="relative">
                    {caseItem.photos && caseItem.photos.length > 0 ? (
                      <img 
                        src={caseItem.photos[0]} 
                        alt={caseItem.full_name}
                        className="w-full h-64 object-cover"
                      />
                    ) : (
                      <div className="w-full h-64 bg-slate-200 flex items-center justify-center">
                        <Users className="w-16 h-16 text-slate-400" />
                      </div>
                    )}
                    <div className="absolute top-4 left-4 flex gap-2">
                      {caseItem.status === 'active' && (
                        <Badge className="bg-red-600 text-white border-0 status-active">MISSING</Badge>
                      )}
                      {caseItem.status === 'located_safe' && (
                        <Badge className="bg-green-600 text-white border-0">FOUND</Badge>
                      )}
                      {caseItem.is_urgent && (
                        <Badge className="bg-amber-500 text-white border-0">URGENT</Badge>
                      )}
                      {caseItem.is_child && (
                        <Badge className="bg-purple-600 text-white border-0">CHILD</Badge>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{caseItem.full_name}</h3>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      {caseItem.age && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-700">Age:</span> {caseItem.age} years
                        </div>
                      )}
                      {caseItem.last_seen_location?.city && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {caseItem.last_seen_location.city}, {caseItem.last_seen_location.state}
                        </div>
                      )}
                      {caseItem.last_seen_date && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Last seen: {formatDate(caseItem.last_seen_date)}
                        </div>
                      )}
                    </div>
                    <Button className="w-full mt-4 bg-primary hover:bg-primary/90 rounded-full btn-press">
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {recentCases.length === 0 && (
            <div className="text-center py-16">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-muted-foreground">No cases reported yet</p>
            </div>
          )}

          <div className="mt-8 text-center md:hidden">
            <Link to="/search">
              <Button variant="outline" className="rounded-full">
                View All Cases
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">How It Works</h2>
            <p className="text-muted-foreground mt-2 max-w-xl mx-auto">
              Join thousands of people helping reunite families
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Report</h3>
              <p className="text-muted-foreground">
                Submit detailed information about a missing person with photos and last known location
              </p>
            </div>
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Search className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Search</h3>
              <p className="text-muted-foreground">
                Browse active cases in your area or search for specific missing persons
              </p>
            </div>
            <div className="text-center p-8">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Eye className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Report Sighting</h3>
              <p className="text-muted-foreground">
                Seen someone? Submit a sighting report with photos and location details
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 map-pattern opacity-10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Every Second Counts
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Your vigilance could be the key to reuniting a family. Join our community and help make a difference.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={isAuthenticated ? "/report" : "/register"}>
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8 btn-press">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Report Missing Person
              </Button>
            </Link>
            <Link to="/search">
              <Button size="lg" variant="outline" className="rounded-full px-8 text-white border-white/30 hover:bg-white/10 btn-press">
                View Active Cases
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer is rendered globally by App.js (GlobalFooter). Do not
          re-render here or it will appear twice on the homepage. */}
    </div>
  );
}
